import BootstrapTheme from '@fullcalendar/bootstrap';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { config } from '../../../config/index';
import { POST, newFetch } from '../../../helpers/fetch';

const { SERVER_URL } = config;

const AssociateLeaves = ({ activeDate, siteAssociates, type }) => {
  const [associateLeaves, setAssociateLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment().format('YYYY-MM-DD'));

  // Fetch associates on leave for the selected date
  const fetchAssociateLeaves = async () => {
    if (!siteAssociates || siteAssociates.length === 0) return;

    setLoading(true);
    try {
      const response = await newFetch(POST, '/associate-leaves/by-date', {
        siteAssociates,
        start_date: moment(activeDate).format('YYYY-MM-DD'),
        type: type,
      });

      if (response.success) {
        let data = [];
        let cnt = 0;
        if (response.data.length > 0) {
          for (let row of response.data) {
            let coverage = '';
            if (row.leave_coverage === 1) {
              coverage = '-AM';
            } else if (row.leave_coverage === 2) {
              coverage = '-PM';
            }

            // Fix end date — FullCalendar's end is exclusive, so add 1 day
            data.push({
              id: row.id,
              title: `${row.associate.first_name} ${row.associate.last_name} - (${row.leave_type}${coverage})`,
              start: row.start_date,
              end: moment(row.end_date).add(1, 'days').format('YYYY-MM-DD'),
              className: 'bg-primary text-white',
              priority: 2,
            });
            cnt++;
          }
          setDataSource(data);
        } else {
          setDataSource([]);
        }

        setAssociateLeaves(response.data);
      } else {
        setAssociateLeaves([]);
      }
    } catch (error) {
      console.error('Error fetching employee leaves:', error);
      setAssociateLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEventDrop = async (info) => {
    const formatedStartDate = moment(info.event.start).format('YYYY-MM-DD');

    await props.actionCreator({
      type: types.UPDATE_WORKER_SCHEDULE,
      payload: {
        id: info.event.id,
        shift_date: formatedStartDate,
        dateRange: dateRange,
      },
    });
  };

  const handleEventClick = (info) => {
    const workerScheduleID = parseInt(info.event.id);
    const filteredWorkerSchedule = scheduling.data.filter(
      (item) => item.id === workerScheduleID,
    );

    if (filteredWorkerSchedule.length > 0) {
      const workerScheduleData = filteredWorkerSchedule[0];

      setSelectedWorkerSchedule({
        ...workerScheduleData,
        shift_date: workerScheduleData.shift_date_string,
      });

      let currentWorkerOptions = [
        {
          label: workerScheduleData.worker_name,
          value: workerScheduleData.worker_name,
        },
      ];

      let defaultWorkerOptions = [];

      setGroups({
        workers: [workerScheduleData.worker_id],
        currentWorkerOptions,
        defaultWorkerOptions,
        isRequiredError: false,
        isDuplicateError: false,
      });

      setIsEdit(true);
      toggle();
    }
  };

  const handleWorkerClicks = (arg) => {
    const date = moment(arg['date']).format('YYYY-MM-DD');
    let defaultWorkerOptions = [];

    if (workers.data) {
      for (let worker of workers.data) {
        defaultWorkerOptions.push({
          label: worker.worker_name,
          value: worker.worker_name,
        });
      }
    }

    let currentWorkerOptions = [];

    setGroups((prevGroup) => {
      const newGroup = {
        ...prevGroup,
        workers: null,
        currentWorkerOptions,
        defaultWorkerOptions,
        isRequiredError: false,
        isDuplicateError: false,
      };
      return newGroup;
    });

    setSelectedWorkerSchedule({ shift_date: date });
    setIsEdit(false);
    toggle();
  };

  const customEventOrder = (a, b) => {
    // Sort by priority first, then by start time
    if (a.extendedProps.priority !== b.extendedProps.priority) {
      return a.extendedProps.priority - b.extendedProps.priority;
    }
    return new Date(a.start) - new Date(b.start);
  };

  // Format date label based on type
  const getDateLabel = () => {
    if (type === 'month') {
      return moment(activeDate).format('MMMM YYYY');
    }
    return moment(activeDate).format('MMMM D, YYYY');
  };

  useEffect(() => {
    if (activeDate && siteAssociates.length > 0) {
      fetchAssociateLeaves();
      setCurrentDate(moment(activeDate).format('YYYY-MM-DD'));
    } else {
      setAssociateLeaves([]);
    }
  }, [activeDate, siteAssociates]);

  return (
    <div className="bg-white rounded mb-4 border p-4">
      <h5 className="mb-3">Leave Calendar</h5>

      <style>{`
  .fc-daygrid-event .fc-event-title {
    white-space: normal !important;
    overflow: visible !important;
    word-break: break-word;
    line-height: 1.3;
  }
  .fc-daygrid-event {
    align-items: flex-start !important;
    height: auto !important;
  }
`}</style>

      {loading ? (
        <div className="text-center text-muted my-3">
          <i>Loading...</i>
        </div>
      ) : associateLeaves.length === 0 ? (
        <div className="text-center text-muted my-3">
          <i>No record found</i>
        </div>
      ) : (
        <div className="row">
          <FullCalendar
            plugins={[
              BootstrapTheme,
              dayGridPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            headerToolbar={{
              left: '', // removes "prev" button area
              center: 'title', // keep only the month title
              right: '', // removes "next" button area
            }}
            slotDuration={'00:15:00'}
            initialDate={currentDate}
            handleWindowResize={true}
            themeSystem="bootstrap"
            initialView="dayGridMonth"
            events={dataSource}
            editable={false}
            droppable={false}
            selectable={false}
          />
        </div>
      )}
    </div>
  );
};

export default AssociateLeaves;
